import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;
const AUTH_TOKEN_KEY = "narwhal_auth_token";

export const api = axios.create({
  baseURL: API,
  headers: { "Content-Type": "application/json" },
});

// Attach Bearer token from localStorage on every request
api.interceptors.request.use((config) => {
  const token = localStorage.getItem(AUTH_TOKEN_KEY);
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Bounce to /login on 401 (except for the login request itself)
api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err?.response?.status === 401 && !err.config?.url?.includes("/auth/login")) {
      localStorage.removeItem(AUTH_TOKEN_KEY);
      if (typeof window !== "undefined" && window.location.pathname !== "/login") {
        window.location.href = "/login";
      }
    }
    return Promise.reject(err);
  }
);

// ---- Auth helpers ----
export const setAuthToken = (token) => {
  if (token) localStorage.setItem(AUTH_TOKEN_KEY, token);
  else localStorage.removeItem(AUTH_TOKEN_KEY);
};
export const getAuthToken = () => localStorage.getItem(AUTH_TOKEN_KEY);
export const authLogin = (email, password) =>
  api.post("/auth/login", { email, password }).then((r) => r.data);
export const authMe = () => api.get("/auth/me").then((r) => r.data);
export const authLogout = () => api.post("/auth/logout").then((r) => r.data);

// ---- Users management (narwhal admin) ----
export const fetchUsers = () => api.get("/users").then((r) => r.data);
export const createUser = (body) => api.post("/users", body).then((r) => r.data);
export const updateUser = (id, body) => api.patch(`/users/${id}`, body).then((r) => r.data);
export const deleteUser = (id) => api.delete(`/users/${id}`).then((r) => r.data);

export const fetchClients = () => api.get("/clients").then((r) => r.data);
export const fetchClient = (id) => api.get(`/clients/${id}`).then((r) => r.data);
export const fetchGlobalDashboard = (period = 30) =>
  api.get(`/dashboard/global`, { params: { period } }).then((r) => r.data);
export const fetchClientMetrics = (id, period = 30) =>
  api.get(`/clients/${id}/metrics`, { params: { period } }).then((r) => r.data);
export const fetchClientIntegrations = (id) =>
  api.get(`/clients/${id}/integrations`).then((r) => r.data);
export const toggleIntegration = (clientId, platform) =>
  api.post(`/clients/${clientId}/integrations/${platform}/toggle`).then((r) => r.data);
export const syncIntegration = (clientId, platform) =>
  api.post(`/clients/${clientId}/integrations/${platform}/sync`).then((r) => r.data);
export const fetchAlerts = (clientId) =>
  api.get(`/alerts`, { params: clientId ? { client_id: clientId } : {} }).then((r) => r.data);
export const dismissAlert = (id) => api.delete(`/alerts/${id}`).then((r) => r.data);
export const createClient = (payload) => api.post(`/clients`, payload).then((r) => r.data);

export const fetchClientAreas = (id) => api.get(`/clients/${id}/areas`).then((r) => r.data);
export const fetchClientCommunications = (id, monthsBack = 6) =>
  api.get(`/clients/${id}/communications`, { params: { months_back: monthsBack } }).then((r) => r.data);
export const fetchAccountGrowth = (id, monthsBack = 6) =>
  api.get(`/clients/${id}/account-growth`, { params: { months_back: monthsBack } }).then((r) => r.data);
export const fetchQuarterlyCompliance = (id, year, quarter) =>
  api.get(`/clients/${id}/quarterly-compliance`, { params: { year, quarter } }).then((r) => r.data);
export const fetchCompliance = (id, params) =>
  api.get(`/clients/${id}/compliance`, { params }).then((r) => r.data);

// ─────── Visibilidad IA ───────
export const fetchGeoOverview = (id) =>
  api.get(`/clients/${id}/geo/overview`).then((r) => r.data);
export const fetchGeoPrompts = (id) =>
  api.get(`/clients/${id}/geo/prompts`).then((r) => r.data);
export const createGeoPrompt = (id, payload) =>
  api.post(`/clients/${id}/geo/prompts`, payload).then((r) => r.data);
export const updateGeoPrompt = (id, promptId, payload) =>
  api.put(`/clients/${id}/geo/prompts/${promptId}`, payload).then((r) => r.data);
export const deleteGeoPrompt = (id, promptId) =>
  api.delete(`/clients/${id}/geo/prompts/${promptId}`).then((r) => r.data);
export const seedBrandGeoPrompts = (id) =>
  api.post(`/clients/${id}/geo/prompts/seed-brand`).then((r) => r.data);
export const fetchGeoBrandConfig = (id) =>
  api.get(`/clients/${id}/geo/brand-config`).then((r) => r.data);
export const updateGeoBrandConfig = (id, payload) =>
  api.put(`/clients/${id}/geo/brand-config`, payload).then((r) => r.data);
export const fetchGeoRuns = (id) =>
  api.get(`/clients/${id}/geo/runs`).then((r) => r.data);
export const fetchGeoRun = (id, runId) =>
  api.get(`/clients/${id}/geo/runs/${runId}`).then((r) => r.data);
export const triggerGeoRun = (id, payload = {}) =>
  api.post(`/clients/${id}/geo/run`, payload).then((r) => r.data);
export const fetchGeoSnapshot = (id) =>
  api.get(`/clients/${id}/geo/snapshot`).then((r) => r.data);
export const pinGeoSnapshot = (id, runId) =>
  api.post(`/clients/${id}/geo/snapshot/${runId}`).then((r) => r.data);
export const fetchGeoEvolution = (id, limit = 12) =>
  api.get(`/clients/${id}/geo/evolution`, { params: { limit } }).then((r) => r.data);
export const fetchGeoMonthlyMatrix = (id, baselineYm = "2026-07") =>
  api.get(`/clients/${id}/geo/monthly-matrix`, { params: { baseline_ym: baselineYm } }).then((r) => r.data);
export const deleteGeoRun = (id, runId) =>
  api.delete(`/clients/${id}/geo/runs/${runId}`).then((r) => r.data);
export const duplicateGeoPromptByCountries = (id, promptId, countries) =>
  api.post(`/clients/${id}/geo/prompts/${promptId}/duplicate-countries`, { countries }).then((r) => r.data);
export const createGeoSyntheticRun = (id, payload) =>
  api.post(`/clients/${id}/geo/synthetic-run`, payload).then((r) => r.data);
export const exploreGeoPrompt = (id, payload) =>
  api.post(`/clients/${id}/geo/explore`, payload, { timeout: 180000 }).then((r) => r.data);
export const fetchGeoExploreHistory = (id, limit = 20) =>
  api.get(`/clients/${id}/geo/explore/history`, { params: { limit } }).then((r) => r.data);
export const deleteGeoExplore = (id, itemId) =>
  api.delete(`/clients/${id}/geo/explore/${itemId}`).then((r) => r.data);


export const fetchGrowthCorporate = (id, params) =>
  api.get(`/clients/${id}/growth-corporate`, { params }).then((r) => r.data);

export const fetchGrowthPersonal = (id, params) =>
  api.get(`/clients/${id}/growth-personal`, { params }).then((r) => r.data);

export const fetchSeoData = (id) =>
  api.get(`/clients/${id}/seo`).then((r) => r.data);
export const saveSeoData = (id, payload) =>
  api.put(`/clients/${id}/seo`, payload).then((r) => r.data);

export const fetchCroData = (id) =>
  api.get(`/clients/${id}/cro`).then((r) => r.data);
export const saveCroData = (id, payload) =>
  api.put(`/clients/${id}/cro`, payload).then((r) => r.data);
export const fetchCroWeekly = (id) =>
  api.get(`/clients/${id}/cro-weekly`).then((r) => r.data);
export const fetchGoogleAds = (id, dateFrom, dateTo, network = "adwords") =>
  api
    .get(`/clients/${id}/google-ads`, { params: { date_from: dateFrom, date_to: dateTo, network } })
    .then((r) => r.data);

// ─────── Business Development ───────
export const fetchBdData = (id) => api.get(`/clients/${id}/bd`).then((r) => r.data);
export const upsertBdData = (id, payload) => api.put(`/clients/${id}/bd`, payload).then((r) => r.data);
export const fetchBdSummary = (id) => api.get(`/clients/${id}/bd/summary`).then((r) => r.data);
export const fetchBdBriefs = (id) => api.get(`/clients/${id}/bd/briefs`).then((r) => r.data);
export const fetchBdInboundLeads = (id) => api.get(`/clients/${id}/bd/inbound-leads`).then((r) => r.data);
export const fetchBdBriefsDoc = (id) => api.get(`/clients/${id}/bd/briefs-doc`).then((r) => r.data);
export const fetchBdPorFuera = (id) => api.get(`/clients/${id}/bd/por-fuera`).then((r) => r.data);

// ---- Skylead / Narwhalhub (Multilead Open API) ----
export const fetchSkyleadAllAccounts = () => api.get(`/skylead/accounts`).then((r) => r.data);
export const fetchSkyleadOverview = (id, accountId = null) =>
  api.get(`/clients/${id}/skylead/overview`, { params: accountId ? { account_id: accountId } : {} }).then((r) => r.data);
export const fetchSkyleadCampaigns = (id) => api.get(`/clients/${id}/skylead/campaigns`).then((r) => r.data);
export const fetchSkyleadTimeline = (id, days = 30, accountId = null) =>
  api.get(`/clients/${id}/skylead/timeline`, { params: { days, ...(accountId ? { account_id: accountId } : {}) } }).then((r) => r.data);
export const fetchSkyleadTags = (id) => api.get(`/clients/${id}/skylead/tags`).then((r) => r.data);
export const fetchSkyleadLeadsByTag = (id, maxPages = 30) =>
  api.get(`/clients/${id}/skylead/leads-by-tag`, { params: { max_pages: maxPages } }).then((r) => r.data);
export const fetchSkyleadConversation = (id, accountId, thread) =>
  api.get(`/clients/${id}/skylead/conversation`, { params: { account_id: accountId, thread } }).then((r) => r.data);

// ---- AI Outreach (Narwhal only, on-demand) ----
export const NARWHAL_CLIENT_ID = "64d81890-a8d3-4c2a-9291-be0c7fffe6b7";
export const saveKeywordRankingsSnapshot = (id, body) =>
  api.post(`/clients/${id}/seo/keyword-rankings/snapshot`, body).then((r) => r.data);

// ---- Google Analytics 4 ----
export const fetchGa4Overview = (body) =>
  api.post(`/google/analytics/overview`, body, { timeout: 60000 }).then((r) => r.data);

// ---- Google Ads (direct API) ----
export const fetchGoogleAdsCampaignsDirect = (body) =>
  api.post(`/google/ads/campaigns`, body, { timeout: 60000 }).then((r) => r.data);
export const fetchGoogleAdsSearchTermsDirect = (body) =>
  api.post(`/google/ads/search-terms`, body, { timeout: 60000 }).then((r) => r.data);
export const fetchGoogleAdsOverview = (body) =>
  api.post(`/google/ads/overview`, body, { timeout: 90000 }).then((r) => r.data);
export const fetchGoogleAdsAccountSummary = (customerId) =>
  api.get(`/google/ads/account-summary`, { params: { customer_id: customerId }, timeout: 60000 }).then((r) => r.data);
export const fetchGoogleAdsAdGroups = (body) =>
  api.post(`/google/ads/ad-groups`, body, { timeout: 60000 }).then((r) => r.data);
export const fetchGoogleAdsKeywords = (body) =>
  api.post(`/google/ads/keywords`, body, { timeout: 60000 }).then((r) => r.data);
export const fetchGoogleAdsAds = (body) =>
  api.post(`/google/ads/ads`, body, { timeout: 60000 }).then((r) => r.data);
export const fetchGoogleAdsSegments = (body) =>
  api.post(`/google/ads/segments`, body, { timeout: 60000 }).then((r) => r.data);

// ---- Google Search Console (direct API) ----
export const fetchSearchConsoleSites = () =>
  api.get(`/google/search-console/sites`).then((r) => r.data);
export const fetchSearchConsoleQuery = (body) =>
  api.post(`/google/search-console/query`, body, { timeout: 60000 }).then((r) => r.data);
export const fetchSearchConsoleSitemaps = (siteUrl) =>
  api.get(`/google/search-console/sitemaps`, { params: { site_url: siteUrl }, timeout: 30000 }).then((r) => r.data);
export const inspectSearchConsoleUrl = (body) =>
  api.post(`/google/search-console/inspect`, body, { timeout: 30000 }).then((r) => r.data);
export const saveClientGscSiteUrl = (clientId, siteUrl) =>
  api.post(`/clients/${clientId}/cro/gsc-site-url`, { site_url: siteUrl }).then((r) => r.data);

// ---- Google PageSpeed Insights (Core Web Vitals) ----
export const fetchPageSpeed = (body) =>
  api.post(`/google/pagespeed`, body, { timeout: 90000 }).then((r) => r.data);
export const fetchPageSpeedBatch = (body) =>
  api.post(`/google/pagespeed/batch`, body, { timeout: 180000 }).then((r) => r.data);
export const fetchAiOutreachFocuses = () =>
  api.get(`/ai/outreach/focuses`).then((r) => r.data);
export const fetchAiOutreachAccounts = (id) =>
  api.get(`/clients/${id}/ai/outreach/accounts`).then((r) => r.data);
export const runAiOutreachAnalysis = (id, body) =>
  api.post(`/clients/${id}/ai/outreach/analyze`, body).then((r) => r.data);
export const fetchAiOutreachAnalysisJob = (id, jobId) =>
  api.get(`/clients/${id}/ai/outreach/analyze/${jobId}`).then((r) => r.data);


// ---- YouTube via Metricool ----
export const fetchYoutubeOverview = (id, dateFrom, dateTo) =>
  api.get(`/clients/${id}/youtube/overview`, { params: { date_from: dateFrom, date_to: dateTo } }).then((r) => r.data);
export const fetchYoutubeVideos = (id, dateFrom, dateTo, postsType = "all") =>
  api.get(`/clients/${id}/youtube/videos`, { params: { date_from: dateFrom, date_to: dateTo, posts_type: postsType } }).then((r) => r.data);
export const fetchYoutubeDemographics = (id) =>
  api.get(`/clients/${id}/youtube/demographics`).then((r) => r.data);
export const fetchYoutubeCompetitors = (id, dateFrom, dateTo) =>
  api.get(`/clients/${id}/youtube/competitors`, { params: { date_from: dateFrom, date_to: dateTo } }).then((r) => r.data);

// ---- Instagram via Metricool ----
export const fetchInstagramOverview = (id, dateFrom, dateTo) =>
  api.get(`/clients/${id}/instagram/overview`, { params: { date_from: dateFrom, date_to: dateTo } }).then((r) => r.data);
export const fetchInstagramPosts = (id, dateFrom, dateTo) =>
  api.get(`/clients/${id}/instagram/posts`, { params: { date_from: dateFrom, date_to: dateTo } }).then((r) => r.data);
export const fetchInstagramReels = (id, dateFrom, dateTo) =>
  api.get(`/clients/${id}/instagram/reels`, { params: { date_from: dateFrom, date_to: dateTo } }).then((r) => r.data);
export const fetchInstagramDemographics = (id, dateFrom, dateTo) =>
  api.get(`/clients/${id}/instagram/demographics`, { params: { date_from: dateFrom, date_to: dateTo } }).then((r) => r.data);

// ---- Meta Ads (Facebook + Instagram Ads) via Metricool ----
export const fetchMetaAdsOverview = (id, dateFrom, dateTo) =>
  api.get(`/clients/${id}/meta-ads/overview`, { params: { date_from: dateFrom, date_to: dateTo } }).then((r) => r.data);
export const fetchMetaAdsCampaigns = (id, dateFrom, dateTo, provider, status) =>
  api.get(`/clients/${id}/meta-ads/campaigns`, {
    params: { date_from: dateFrom, date_to: dateTo, provider: provider || undefined, status: status || undefined },
  }).then((r) => r.data);

// ---- Clients integration status ----
export const fetchClientsIntegrationStatus = () =>
  api.get(`/clients-integration-status`).then((r) => r.data);

// ---- Notion overview ----
export const fetchClientNotionOverview = (id) =>
  api.get(`/clients/${id}/notion/overview`).then((r) => r.data);

export const archiveNotionTask = (clientId, taskId) =>
  api.post(`/clients/${clientId}/notion/tasks/${encodeURIComponent(taskId)}/archive`).then((r) => r.data);
export const unarchiveNotionTask = (clientId, taskId) =>
  api.delete(`/clients/${clientId}/notion/tasks/${encodeURIComponent(taskId)}/archive`).then((r) => r.data);

export const fetchSheetSyncStatus = () =>
  api.get(`/sync/google-sheets/status`).then((r) => r.data);
export const triggerSheetSync = () =>
  api.post(`/sync/google-sheets/run`).then((r) => r.data);
export const fetchCommsPlans = (id) =>
  api.get(`/clients/${id}/comms-plans`).then((r) => r.data);
export const createCommsPlan = (id, payload) =>
  api.post(`/clients/${id}/comms-plans`, payload).then((r) => r.data);
export const updateCommsPlan = (id, planId, payload) =>
  api.put(`/clients/${id}/comms-plans/${planId}`, payload).then((r) => r.data);
export const deleteCommsPlan = (id, planId) =>
  api.delete(`/clients/${id}/comms-plans/${planId}`).then((r) => r.data);
export const fetchPublications = (id, filters = {}) =>
  api.get(`/clients/${id}/publications`, { params: filters }).then((r) => r.data);
export const createPublication = (id, payload) =>
  api.post(`/clients/${id}/publications`, payload).then((r) => r.data);
export const deletePublication = (id, pubId) =>
  api.delete(`/clients/${id}/publications/${pubId}`).then((r) => r.data);
export const fetchCommitments = (id) =>
  api.get(`/clients/${id}/commitments`).then((r) => r.data);
export const updateCommitments = (id, payload) =>
  api.put(`/clients/${id}/commitments`, payload).then((r) => r.data);

export const CONTENT_TYPE_META = {
  Corpo: { label: "Perfil Corporativo", short: "Corporativo", color: "#2563EB" },
  Personal: { label: "Perfil Personal", short: "Personal", color: "#0F172A" },
  "News L": { label: "Newsletter email marketing", short: "Newsletter email", color: "#7C3AED" },
  Blog: { label: "Blog web", short: "Blog web", color: "#0EA5E9" },
  Instagram: { label: "Instagram", short: "Instagram", color: "#EC4899" },
  "Lead Magnet": { label: "Lead Magnets", short: "Lead Magnets", color: "#F59E0B" },
  Community: { label: "Community", short: "Community", color: "#10B981" },
  YouTube: { label: "YouTube", short: "YouTube", color: "#EF4444" },
  "Blog LI": { label: "Blog LinkedIn", short: "Blog LI", color: "#0077B5" },
  "News LI": { label: "Newsletter LinkedIn", short: "Newsletter LI", color: "#1D4ED8" },
};

export const PLATFORM_META = {
  metricool: { name: "Metricool", desc: "Social media analytics", color: "#FF5C5C" },
  semrush: { name: "SEMrush", desc: "SEO & competitive research", color: "#FF642D" },
  search_console: { name: "Search Console", desc: "Google search performance", color: "#4285F4" },
  google_analytics: { name: "Google Analytics 4", desc: "Web & app analytics", color: "#E37400" },
  skylead: { name: "Outreach", desc: "LinkedIn outreach automation", color: "#0A66C2" },
  google_sheets: { name: "Google Sheets", desc: "Private spreadsheet sync", color: "#0F9D58" },
};

export const formatNumber = (n) => {
  if (n === null || n === undefined) return "—";
  if (Math.abs(n) >= 1_000_000) return (n / 1_000_000).toFixed(1) + "M";
  if (Math.abs(n) >= 1_000) return (n / 1_000).toFixed(1) + "k";
  if (Number.isInteger(n)) return n.toLocaleString();
  return n.toFixed(2);
};
